import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { IHSANROLL_PROGRAMME } from '../../data/ihsanRollMock';
import {
  attendanceKey,
  blankSheet,
  formatDateLabel,
  formatTime,
  saveAttendance,
  todayISO
} from '../../lib/ihsanRollStore';
import MorphButton from '../../components/MorphButton';
import { fieldClass } from './shared';

export default function AttendanceView({ store, caseNo, update, onDone }) {
  const date = todayISO();
  const key = attendanceKey(caseNo, date);
  const saved = store.attendance[key];
  const officer = store.dutyOfficer;

  const [items, setItems] = useState(() => {
    if (saved?.items) {
      return { ...blankSheet(), ...saved.items };
    }
    return blankSheet();
  });

  const allAttended = useMemo(
    () => IHSANROLL_PROGRAMME.every((p) => items[p.id]?.status === 'attended'),
    [items]
  );

  const setStatus = (id, status) => {
    setItems((prev) => ({ ...prev, [id]: { ...prev[id], status } }));
  };

  const setRemarks = (id, remarks) => {
    setItems((prev) => ({ ...prev, [id]: { ...prev[id], remarks } }));
  };

  const toggleMaster = () => {
    if (allAttended) {
      setItems(blankSheet());
      return;
    }
    setItems(
      IHSANROLL_PROGRAMME.reduce((acc, p) => {
        acc[p.id] = { status: 'attended', remarks: items[p.id]?.remarks || '' };
        return acc;
      }, {})
    );
  };

  const submit = () => {
    if (!officer) {
      toast.error('Select a duty officer first', { position: 'top-center' });
      return false;
    }
    const unset = IHSANROLL_PROGRAMME.filter((p) => !items[p.id]?.status);
    if (unset.length) {
      toast.error('Mark every programme item attended or absent', { position: 'top-center' });
      return false;
    }
    update((prev) => saveAttendance(prev, caseNo, date, items, officer));
    return true;
  };

  return (
    <div className="px-4 pt-5 pb-8" data-testid="ihsanroll-attendance">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Case #{caseNo} · {formatDateLabel(date)} · {officer || 'no officer'}
      </p>
      <h1 className="text-2xl font-extrabold text-[#0F172A] mt-1">Attendance</h1>
      {saved && (
        <p className="text-xs text-muted-foreground mt-1">
          Last saved by {saved.savedBy}
        </p>
      )}

      <label className="mt-4 flex min-h-[44px] items-center gap-3 rounded-xl border border-border bg-white px-3">
        <input
          type="checkbox"
          data-testid="ihsanroll-mark-all"
          checked={allAttended}
          onChange={toggleMaster}
          className="h-4 w-4 accent-primary"
        />
        <span className="text-sm font-semibold text-[#0F172A]">Mark all attended for the day</span>
      </label>

      <div className="mt-4 flex flex-col gap-3">
        {IHSANROLL_PROGRAMME.map((item) => {
          const row = items[item.id] || { status: null, remarks: '' };
          return (
            <div
              key={item.id}
              className="rounded-2xl border border-border bg-white p-3"
              data-testid={`ihsanroll-programme-${item.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] font-semibold text-primary">{formatTime(item.time)}</p>
                  <p className="text-sm font-bold text-[#0F172A]">{item.label}</p>
                </div>
                <div className="flex gap-1">
                  {['attended', 'absent'].map((status) => {
                    const active = row.status === status;
                    return (
                      <button
                        key={status}
                        type="button"
                        aria-pressed={active}
                        data-testid={`ihsanroll-${item.id}-${status}`}
                        onClick={() => setStatus(item.id, status)}
                        className={`h-9 px-3 rounded-full text-[11px] font-bold capitalize ${
                          active
                            ? status === 'attended'
                              ? 'bg-primary text-white'
                              : 'bg-[#D64545] text-white'
                            : 'bg-muted/60 text-[#0F172A]'
                        }`}
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>
              </div>
              <input
                type="text"
                aria-label={`Remarks for ${item.label}`}
                placeholder="Remarks"
                value={row.remarks || ''}
                onChange={(e) => setRemarks(item.id, e.target.value)}
                className={`${fieldClass} h-9 text-xs`}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-5">
        <MorphButton
          data-testid="ihsanroll-save-attendance"
          idleLabel="Save attendance"
          successLabel="Attendance saved"
          onClick={submit}
          onComplete={onDone}
          className="w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover"
        />
      </div>
    </div>
  );
}
